/* The bottom rail — `Builder planu - tydzien - programator.dc.html`'s last row, transcribed:
 *
 *   flex:none; display:flex; align-items:flex-start; gap:16px; padding:14px 20px 16px;
 *   background:var(--bg-card); border-top:1px solid var(--border)
 *
 * ── Three departures were reversed on 8 September 2026 ───────────────────────────────────────
 * · **The ⓘ is gone.** The caption carried a hint popover holding the 1,0 / 0,5 weighting. The mock
 *   draws no ⓘ. The weighting is now stated nowhere on this screen; flagged for the owner.
 * · **The rail no longer keeps out from under the day panel.** It took `data-panel` and shrank its
 *   grid so the last two cells stayed clear of the 574px panel. The mock's grid runs the full width
 *   and the panel covers its last cells, and that is what ships.
 * · **The over-MRV band is the mock's `#ff5a1f`,** not `--vol-over-ink`. The substitution was made
 *   on a contrast reading (4.40:1 for the 9px uppercase word) and rejected with the rest of the
 *   derived values. The colour is the BAR and the WORD both, as the mock writes it. */

.rail {
  display: flex;
  flex: none;
  gap: var(--space-4);
  align-items: flex-start;
  padding: var(--space-14px) var(--space-20px) var(--space-4);
  background: var(--bg-card);
  border-top: var(--border-hairline) solid var(--border);
}

.caption {
  display: flex;
  flex: none;
  flex-direction: column;
  gap: var(--space-3px);
  width: 132px;
}


.captionTitle {
  font-size: var(--fs-label-sm);
  font-weight: var(--fw-bold);
  letter-spacing: var(--ls-lab);
  text-transform: uppercase;
  color: var(--text-3);
  white-space: nowrap;
}

.captionSub {
  font-size: var(--fs-micro-label);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-hint);
  color: var(--text-3);
}

/* The scale and the unit, one line each. Two BOXES rather than one run broken by `<br>`, so the two
 * facts do not run together in speech — see the component. The type and the leading stay on the
 * parent, so the drawn result is byte-identical to the wrapped run it replaces. */
.captionLine {
  display: block;
}

/* Six cells across the whole remaining width, `10px 18px` apart. An open day panel covers the last
 * of them, exactly as the mock draws it. */
.cells {
  display: grid;
  flex: 1;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: var(--space-10px) var(--space-5);
  min-width: 0;
  margin: var(--space-0);
  padding: var(--space-0);
  list-style: none;
}

.cell {
  display: flex;
  flex-direction: column;
  gap: var(--space-5px);
  min-width: 0;
}

.head {
  display: flex;
  gap: var(--space-6px);
  align-items: baseline;
  min-width: 0;
}

.dot {
  display: block;
  flex: none;
  width: 7px;
  height: 7px;
  border-radius: var(--radius-hair);
}

.label {
  overflow: hidden;
  font-size: var(--fs-caption);
  font-weight: var(--fw-bold);
  color: var(--text);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.spacer {
  flex: 1;
}

.value {
  font-size: var(--fs-caption);
  font-weight: var(--fw-extrabold);
  color: var(--text);
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

.track {
  position: relative;
  display: block;
  height: 6px;
  overflow: hidden;
  background: var(--bg-sunken);
  border-radius: var(--radius-bar);
}

/* The mock's `transition: width .35s ease` — the one thing on this rail that moves, and it moves
 * only when the trainer changes the week under it. */
.fill {
  display: block;
  height: 100%;
  background: var(--vol-ink);
  border-radius: var(--radius-bar);
  transition-property: width;
  transition-duration: var(--dur-volume-bar);
  transition-timing-function: var(--ease-default);
}

/* The two hairlines, at MEV and at MAV. `--tick-mev` and `--tick-mav` are the mock's own alphas. */
.tickMev,
.tickMav {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 1px;
}

.tickMev {
  background: var(--tick-mev);
}

.tickMav {
  background: var(--tick-mav);
}

/* "W ZAKRESIE" / "POWYŻEJ MAV" / "PONAD MRV" / "PONIŻEJ MEV", 9 / 700 / .08em uppercase, in the
 * cell's own band colour — the same value the bar is filled with, which is what the mock does. */
.status {
  font-size: var(--fs-pl-micro);
  font-weight: var(--fw-bold);
  letter-spacing: var(--ls-group);
  text-transform: uppercase;
  color: var(--vol-ink);
  white-space: nowrap;
}

/* The four bands. One custom property per cell, read by the bar and by the word. */
.belowMev {
  --vol-ink: var(--text-3);
}

.inRange {
  --vol-ink: var(--vol-in-range);
}

.aboveMav {
  --vol-ink: var(--vol-above);
}

.aboveMrv {
  --vol-ink: var(--vol-over);
}

/* The reserve the rail holds while the catalogue read is open. The GATE hides the bars and never
 * the box: the six cells keep their slots from the first frame and a fast answer draws no skeleton
 * at all. */
.labelBar {
  display: block;
  flex: 1;
  height: 9px;
  background: var(--bg-sunken);
  border-radius: var(--radius-bar);
}

.statusBar {
  display: block;
  width: 60%;
  height: 8px;
  background: var(--bg-sunken);
  border-radius: var(--radius-bar);
}

/* In the accessibility tree, drawn nowhere — the "still loading" word beside `aria-busy`. */
.hidden {
  position: absolute;
  width: var(--space-1px);
  height: var(--space-1px);
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}
